import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Calendar, RangeCalendar } from '@heroui/react';
import { today, getLocalTimeZone, getWeeksInMonth, CalendarDate } from '@internationalized/date';

const calendarDateToMoment = date => moment(`${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`, 'YYYY-MM-DD');

const momentToCalendarDate = m => (m && moment.isMoment(m) && m.isValid() ?
  new CalendarDate(m.year(), m.month() + 1, m.date())
  : null);

// Measure one rendered week-row inside the calendar grid so the spacer we
// render below short (5-row) months tops them up to 6 rows exactly — without
// hard-coding a row height that drifts from HeroUI's tokens.
const useCalendarRowSpacer = (containerRef, focusedDate, locale) => {
  const [rowHeight, setRowHeight] = useState(0);
  const weeks = getWeeksInMonth(focusedDate, locale);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) {return undefined;}
    const measure = () => {
      const row = node.querySelector('tbody > tr');
      if (row) {
        const next = row.getBoundingClientRect().height;
        if (next > 0) {setRowHeight(prev => (Math.abs(prev - next) > 0.5 ? next : prev));}
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [containerRef, focusedDate]);

  return rowHeight > 0 ? (6 - weeks) * rowHeight : 0;
};

// Anchor the popover to a trigger element using viewport-relative coordinates
// (position: fixed). Recomputes on scroll/resize so the popover stays glued to
// the trigger as the page scrolls. If there isn't enough room below the
// trigger to fit the popover, places it above instead.
const POPOVER_GAP = 4;
const VIEWPORT_MARGIN = 8;

const usePopoverPosition = (triggerEl, isOpen) => {
  const popoverRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, ready: false });

  useLayoutEffect(() => {
    if (!isOpen || !triggerEl) {return undefined;}

    const update = () => {
      const triggerRect = triggerEl.getBoundingClientRect();
      const popoverEl = popoverRef.current;
      const popoverHeight = popoverEl ? popoverEl.offsetHeight : 0;
      const popoverWidth = popoverEl ? popoverEl.offsetWidth : 0;
      const vh = window.innerHeight;
      const vw = window.innerWidth;

      const spaceBelow = vh - triggerRect.bottom - VIEWPORT_MARGIN;
      const spaceAbove = triggerRect.top - VIEWPORT_MARGIN;
      const fitsBelow = popoverHeight === 0 || spaceBelow >= popoverHeight + POPOVER_GAP;

      const top = fitsBelow || spaceBelow >= spaceAbove ?
        triggerRect.bottom + POPOVER_GAP
        : triggerRect.top - popoverHeight - POPOVER_GAP;

      let { left } = triggerRect;
      if (popoverWidth > 0 && left + popoverWidth + VIEWPORT_MARGIN > vw) {
        left = Math.max(VIEWPORT_MARGIN, vw - popoverWidth - VIEWPORT_MARGIN);
      }

      setPos({ top, left, ready: true });
    };

    update();
    // A second pass once the popover has rendered and we know its real size.
    const raf = requestAnimationFrame(update);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [triggerEl, isOpen]);

  return [popoverRef, pos];
};

// Close the popover when the user clicks anywhere outside both the popover
// and the trigger, or presses Escape. Listening on mousedown matches the
// existing OutsideClickHandler conventions in this app.
const usePopoverOutsideClick = (popoverRef, triggerEl, isOpen, onClose) => {
  useLayoutEffect(() => {
    if (!isOpen) {return undefined;}
    const handle = event => {
      const popover = popoverRef.current;
      if (popover && popover.contains(event.target)) {return;}
      if (triggerEl && triggerEl.contains(event.target)) {return;}
      onClose();
    };
    const handleKeyDown = event => {
      if (event.key === 'Escape') {onClose();}
    };
    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [popoverRef, triggerEl, isOpen, onClose]);
};

const triggerClassName = [
  'inline-flex w-full min-h-10 items-center justify-between gap-3 rounded-lg border',
  'border-[var(--owner-border-strong)] bg-[var(--owner-surface)] px-3 text-sm leading-5',
  'text-[var(--owner-text)] shadow-sm outline-none transition-colors',
  'hover:border-[var(--tenant-primary)] focus-visible:border-[var(--tenant-primary)]',
  'focus-visible:ring-2 focus-visible:ring-[var(--owner-focus-ring)]',
  'cursor-pointer',
].join(' ');

const popoverClassName = 'fixed z-50 rounded-2xl bg-[var(--owner-surface)] p-5 shadow-2xl';

const CalendarIcon = () => (
  <svg
    aria-hidden="true"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="shrink-0 text-[var(--owner-text-muted)]"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const DateRangePickerPopover = ({ triggerEl, initialValue, minValue, isOutsideRange, onApply, onCancel, onClear }) => {
  const { locale } = useIntl();
  const calendarRef = useRef(null);
  const [draft, setDraft] = useState(initialValue);
  const [focusedDate, setFocusedDate] = useState(() => initialValue?.start || minValue);
  const spacerHeight = useCalendarRowSpacer(calendarRef, focusedDate, locale);
  const [popoverRef, pos] = usePopoverPosition(triggerEl, true);
  usePopoverOutsideClick(popoverRef, triggerEl, true, onCancel);

  return (
    <div
      ref={popoverRef}
      className={`${popoverClassName} w-[320px]`}
      role="dialog"
      style={{ top: pos.top, left: pos.left, visibility: pos.ready ? 'visible' : 'hidden', '--accent': 'var(--tenant-primary)', '--accent-foreground': '#ffffff' }}
    >
      <div ref={calendarRef}>
        <RangeCalendar
          minValue={minValue}
          isDateUnavailable={isOutsideRange ? date => isOutsideRange(calendarDateToMoment(date)) : undefined}
          value={draft}
          focusedValue={focusedDate}
          onFocusChange={setFocusedDate}
          onChange={setDraft}
        >
          {/* Override HeroUI's default header layout so the prev / heading / next
              trio actually spaces apart: heading takes the remaining width and
              centers, with the nav buttons pinned to either end. */}
          <RangeCalendar.Header className="flex items-center justify-between gap-2">
            <RangeCalendar.NavButton slot="previous" />
            <RangeCalendar.Heading className="flex-1 text-center" />
            <RangeCalendar.NavButton slot="next" />
          </RangeCalendar.Header>
          <RangeCalendar.Grid>
            <RangeCalendar.GridHeader>
              {day => <RangeCalendar.HeaderCell>{day}</RangeCalendar.HeaderCell>}
            </RangeCalendar.GridHeader>
            <RangeCalendar.GridBody>
              {date => (
                <RangeCalendar.Cell date={date}>
                  {date.day}
                </RangeCalendar.Cell>
              )}
            </RangeCalendar.GridBody>
          </RangeCalendar.Grid>
        </RangeCalendar>
        {/* Top up 5-row months to the height of a 6-row month so the
            popover doesn't jump when paging across short months. */}
        {spacerHeight > 0 && <div aria-hidden style={{ height: `${spacerHeight}px` }} />}
      </div>
      <div className="mt-4 flex items-center justify-between gap-2">
        <button
          type="button"
          className={[
            'rounded-lg px-3 py-1.5 text-sm font-semibold',
            'text-[var(--owner-text-muted)]',
            'hover:text-[var(--owner-text)] hover:bg-[var(--owner-surface-muted)]',
          ].join(' ')}
          onClick={onClear}
        >
          Clear
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            className={[
              'rounded-lg border border-[var(--owner-border-strong)]',
              'bg-[var(--owner-surface)] px-3 py-1.5 text-sm font-semibold',
              'text-[var(--owner-text)] hover:bg-[var(--owner-surface-muted)]',
            ].join(' ')}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'color-mix(in srgb, var(--tenant-primary) 78%, #000 22%)' }}
            onClick={() => onApply(draft)}
            disabled={!draft || !draft.start || !draft.end}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

DateRangePickerPopover.propTypes = {
  initialValue: PropTypes.object,
  isOutsideRange: PropTypes.func,
  minValue: PropTypes.object.isRequired,
  triggerEl: PropTypes.any,
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

export const DateRangePicker = ({ startDate, endDate, isOutsideRange, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [triggerEl, setTriggerEl] = useState(null);

  const startMoment = startDate && moment.isMoment(startDate) ? startDate : null;
  const endMoment = endDate && moment.isMoment(endDate) ? endDate : null;
  const triggerLabel = startMoment && endMoment ?
    `${startMoment.format('DD MMM YYYY')} → ${endMoment.format('DD MMM YYYY')}`
    : (placeholder || 'Select dates');

  const minValue = useMemo(() => {
    const t = today(getLocalTimeZone());
    if (!isOutsideRange) {return t;}
    for (let i = 0; i <= 365; i++) {
      const candidate = t.add({ days: i });
      if (!isOutsideRange(calendarDateToMoment(candidate))) {return candidate;}
    }
    return t;
  }, [isOutsideRange]);

  const initialValue = startMoment && endMoment ?
    { start: momentToCalendarDate(startMoment), end: momentToCalendarDate(endMoment) }
    : null;

  const handleApply = draft => {
    if (draft && draft.start && draft.end) {
      onChange(calendarDateToMoment(draft.start), calendarDateToMoment(draft.end));
    }
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null, null);
    setOpen(false);
  };

  return (
    <>
      {/* eslint-disable-next-line react/button-has-type */}
      <button ref={setTriggerEl} type="button" className={triggerClassName} onClick={() => setOpen(prev => !prev)}>
        <span className={!(startMoment && endMoment) ? 'text-[var(--owner-text-muted)]' : ''}>
          {triggerLabel}
        </span>
        <CalendarIcon />
      </button>

      {open && (
        <DateRangePickerPopover
          triggerEl={triggerEl}
          initialValue={initialValue}
          minValue={minValue}
          isOutsideRange={isOutsideRange}
          onApply={handleApply}
          onCancel={() => setOpen(false)}
          onClear={handleClear}
        />
      )}
    </>
  );
};

DateRangePicker.propTypes = {
  endDate: PropTypes.object,
  isOutsideRange: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  startDate: PropTypes.object,
};

const SingleDatePickerPopover = ({ triggerEl, isOutsideRange, onClose, setDate }) => {
  const { locale } = useIntl();
  const calendarRef = useRef(null);

  const minValue = useMemo(() => {
    const t = today(getLocalTimeZone());
    if (!isOutsideRange) {return t;}
    for (let i = 0; i <= 365; i++) {
      const candidate = t.add({ days: i });
      if (!isOutsideRange(calendarDateToMoment(candidate))) {return candidate;}
    }
    return t;
  }, [isOutsideRange]);

  const [focusedDate, setFocusedDate] = useState(minValue);
  const spacerHeight = useCalendarRowSpacer(calendarRef, focusedDate, locale);
  const [popoverRef, pos] = usePopoverPosition(triggerEl, true);
  usePopoverOutsideClick(popoverRef, triggerEl, true, onClose);

  const handleChange = date => {
    setDate(calendarDateToMoment(date));
    onClose();
  };

  return (
    <div
      ref={popoverRef}
      className={`${popoverClassName} w-[300px]`}
      role="dialog"
      style={{ top: pos.top, left: pos.left, visibility: pos.ready ? 'visible' : 'hidden', '--accent': 'var(--tenant-primary)', '--accent-foreground': '#ffffff' }}
    >
      <div ref={calendarRef}>
        <Calendar
          minValue={minValue}
          isDateUnavailable={isOutsideRange ? date => isOutsideRange(calendarDateToMoment(date)) : undefined}
          focusedValue={focusedDate}
          onFocusChange={setFocusedDate}
          onChange={handleChange}
        >
          {/* Override HeroUI's default header layout so the prev / heading / next
              trio actually spaces apart: heading takes the remaining width and
              centers, with the nav buttons pinned to either end. */}
          <Calendar.Header className="flex items-center justify-between gap-2">
            <Calendar.NavButton slot="previous" />
            <Calendar.Heading className="flex-1 text-center" />
            <Calendar.NavButton slot="next" />
          </Calendar.Header>
          <Calendar.Grid>
            <Calendar.GridHeader>
              {day => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
            </Calendar.GridHeader>
            <Calendar.GridBody>
              {date => (
                <Calendar.Cell date={date}>
                  {date.day}
                </Calendar.Cell>
              )}
            </Calendar.GridBody>
          </Calendar.Grid>
        </Calendar>
        {/* Top up 5-row months to the height of a 6-row month so the
            popover doesn't jump when paging across short months. */}
        {spacerHeight > 0 && <div aria-hidden style={{ height: `${spacerHeight}px` }} />}
      </div>
    </div>
  );
};

SingleDatePickerPopover.propTypes = {
  isOutsideRange: PropTypes.func,
  triggerEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  setDate: PropTypes.func.isRequired,
};

const SingleDatePicker = ({ focused, isOutsideRange, triggerEl, onClose, setDate }) => {
  if (!focused || !triggerEl) {return null;}
  return (
    <SingleDatePickerPopover
      triggerEl={triggerEl}
      isOutsideRange={isOutsideRange}
      onClose={onClose}
      setDate={setDate}
    />
  );
};

SingleDatePicker.propTypes = {
  focused: PropTypes.bool,
  isOutsideRange: PropTypes.func,
  triggerEl: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  setDate: PropTypes.func.isRequired,
};

export default SingleDatePicker;
